import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Hash, Users, ListPlus, X } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { Paywall } from "@/components/Paywall";
import { api, apiError } from "@/lib/api";
import { isActive, useAuth } from "@/lib/auth";

const LANGS = [["fr", "Français"], ["en", "Anglais"], ["es", "Espagnol"], ["pt", "Portugais"], ["ar", "Arabe"], ["de", "Allemand"], ["it", "Italien"]];
const input = "w-full rounded-lg border border-[#ccd0d5] bg-white px-3 py-2 text-sm outline-none focus:border-[#1877f2]";

function splitLinks(text) {
  return text.split(/[\s,;]+/).map(s => s.trim()).filter(s => /^https?:\/\//.test(s))
    .map(u => ({ ...EMPTY, [sourceKey(u)]: u }));
}

export const EMPTY = { name: "", facebook: "", tiktok: "", youtube: "", instagram: "", website: "" };
export const SOURCE_FIELDS = [["facebook", "Lien Facebook"], ["tiktok", "Lien TikTok"], ["youtube", "Chaîne YouTube"],
  ["instagram", "Compte Instagram"], ["website", "Blog ou site (URL)"]];

function sourceKey(u) {
  if (u.includes("tiktok.com")) return "tiktok";
  if (u.includes("facebook.com") || u.includes("fb.com")) return "facebook";
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "youtube";
  if (u.includes("instagram.com")) return "instagram";
  return "website";
}

function KeywordInput({ keywords, setKeywords, max }) {
  const [v, setV] = useState("");
  const add = () => {
    const k = v.trim();
    if (!k || keywords.includes(k)) return setV("");
    if (max && keywords.length >= max) return toast.error(`${max} mots-clés maximum`);
    setKeywords([...keywords, k]);
    setV("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <input className={input} value={v} onChange={e => setV(e.target.value)} placeholder="ex : mariage, clash, Afrobeats…"
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="keyword-input" />
        <button type="button" onClick={add} className="btn-sharp" data-testid="keyword-add-btn"><Plus className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {keywords.map(k => (
          <span key={k} className="chip chip-fb" data-testid={`keyword-chip-${k}`}>
            <Hash className="w-3 h-3" /> {k}
            <button type="button" onClick={() => setKeywords(keywords.filter(x => x !== k))} aria-label="Retirer"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AgentPage() {
  const { user } = useAuth();
  const [agent, setAgent] = useState(null);
  const [bulk, setBulk] = useState("");
  const [saving, setSaving] = useState(false);
  const limits = user?.limits;

  useEffect(() => { api.agent().then(setAgent).catch(e => toast.error(apiError(e))); }, []);
  if (user && !isActive(user)) return (<><AppNav current="agent" /><Paywall /></>);
  if (!agent) return (<><AppNav current="agent" /><div className="p-10 text-sm text-[#65676b]">Chargement…</div></>);

  const set = (k, v) => setAgent(a => ({ ...a, [k]: v }));
  const bloggers = agent.bloggers || [];
  const setBlogger = (i, k, v) => set("bloggers", bloggers.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const addBulk = () => {
    const links = splitLinks(bulk);
    if (!links.length) return toast.error("Aucun lien valide trouvé");
    set("bloggers", [...bloggers, ...links]);
    setBulk("");
    toast.success(`${links.length} profil(s) ajouté(s)`);
  };
  const save = async () => {
    setSaving(true);
    try {
      setAgent(await api.saveAgent({ name: agent.name, niche: agent.niche || "", language: agent.language || "fr", bloggers, keywords: agent.keywords || [] }));
      toast.success("Agent enregistré. Il surveillera ces profils dès la prochaine veille.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };
  const filled = bloggers.filter(b => SOURCE_FIELDS.some(([k]) => b[k])).length;

  return (
    <>
      <AppNav current="agent" />
      <main className="max-w-5xl mx-auto px-4 lg:px-8 py-8 space-y-6" data-testid="agent-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold">Mon agent de veille</h1>
            <p className="text-sm text-[#65676b] mt-1">Choisissez qui surveiller, dans quelle niche et quelle langue. L'agent tourne chaque matin.</p>
          </div>
          <button type="button" onClick={save} disabled={saving} className="btn-primary" data-testid="agent-save-btn">
            <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>

        <section className="card-surface p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block md:col-span-1"><span className="text-xs font-semibold uppercase text-[#65676b]">Nom de l'agent</span>
            <input className={`${input} mt-1`} value={agent.name || ""} onChange={e => set("name", e.target.value)} data-testid="agent-name-input" /></label>
          <label className="block"><span className="text-xs font-semibold uppercase text-[#65676b]">Niche</span>
            <input className={`${input} mt-1`} value={agent.niche || ""} placeholder="ex : potins diaspora, foot, beauté…" onChange={e => set("niche", e.target.value)} data-testid="agent-niche-input" /></label>
          <label className="block"><span className="text-xs font-semibold uppercase text-[#65676b]">Langue des vidéos</span>
            <select className={`${input} mt-1`} value={agent.language || "fr"} onChange={e => set("language", e.target.value)} data-testid="agent-language-select">
              {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
        </section>

        <section className="card-surface p-6">
          <div className="flex items-center gap-2 mb-1"><Hash className="w-5 h-5 text-[#1877f2]" /><h2 className="font-display text-xl font-bold">Mots-clés</h2>
            <span className="chip ml-auto" data-testid="keywords-count">{(agent.keywords || []).length}{limits ? `/${limits.keywords}` : ""}</span></div>
          <p className="text-sm text-[#65676b] mb-4">Les publications qui contiennent ces mots remontent en priorité dans vos buzz.</p>
          <KeywordInput keywords={agent.keywords || []} setKeywords={k => set("keywords", k)} max={limits?.keywords} />
        </section>

        <section className="card-surface p-6">
          <div className="flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-[#1877f2]" /><h2 className="font-display text-xl font-bold">Blogueurs surveillés</h2>
            <span className="chip ml-auto" data-testid="bloggers-limit">{filled}{limits ? `/${limits.profiles}` : ""} profils</span></div>
          <div className="rounded-lg bg-[#f0f2f5] p-4 mb-5">
            <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><ListPlus className="w-4 h-4" /> Ajout rapide : collez des liens Facebook, TikTok, YouTube, Instagram ou de blogs (un par ligne)</p>
            <textarea className={`${input} h-20`} value={bulk} onChange={e => setBulk(e.target.value)} placeholder={"https://www.facebook.com/...\nhttps://www.youtube.com/@...\nhttps://mon-blog.fr"} data-testid="bulk-links-input" />
            <button type="button" onClick={addBulk} className="btn-sharp mt-2" data-testid="bulk-add-btn"><Plus className="w-4 h-4" /> Ajouter ces liens</button>
          </div>
          <div className="space-y-2">
            {bloggers.map((b, i) => (
              <div key={i} className="flex gap-2 items-start rounded-lg border border-[#eef0f2] p-2" data-testid={`blogger-edit-row-${i}`}>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                <input className={input} placeholder="Nom (auto)" value={b.name || ""} onChange={e => setBlogger(i, "name", e.target.value)} data-testid={`blogger-name-${i}`} />
                {SOURCE_FIELDS.map(([k, label]) => (
                  <input key={k} className={input} placeholder={label} value={b[k] || ""} onChange={e => setBlogger(i, k, e.target.value)} data-testid={`blogger-${k}-${i}`} />
                ))}
                </div>
                <button type="button" onClick={() => set("bloggers", bloggers.filter((_, j) => j !== i))} className="btn-ghost !px-3" aria-label="Supprimer" data-testid={`blogger-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => set("bloggers", [...bloggers, { ...EMPTY }])} className="btn-sharp" data-testid="blogger-add-btn">
              <Plus className="w-4 h-4" /> Ajouter un blogueur
            </button>
          </div>
        </section>
      </main>
    </>
  );
}
